import React, { useEffect, useState } from 'react';
import { LegalDocument } from './LegalDocument';
import {
  AcceptanceInput,
  BusinessOrder,
  formatLegalMoney as money,
} from './types';

export function BusinessOrderCheckout({
  order,
  quoteToken,
  draft = false,
  onAccept,
}: {
  order: BusinessOrder;
  quoteToken: string;
  draft?: boolean;
  onAccept: (input: AcceptanceInput) => Promise<void>;
}) {
  const [agreed, setAgreed] = useState(false);
  const [otAgreed, setOtAgreed] = useState(false);
  const [signer, setSigner] = useState({ name: '', email: '', role: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    setAgreed(false);
    setOtAgreed(false);
    setError('');
    setDone(false);
  }, [quoteToken, order]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed || (order.acknowledgments.opentable && !otAgreed)) {
      setError(
        'Accept the business agreement and any required OpenTable authorization.',
      );
      return;
    }
    if (draft) {
      setError(
        'This review draft cannot be accepted. No payment or acceptance was recorded.',
      );
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onAccept({
        quote_token: quoteToken,
        signer,
        business_agreed: agreed,
        opentable_agreed: otAgreed,
      });
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const total =
    order.main_plan.price_cents +
    order.integrations.reduce((n, line) => n + line.price_cents, 0);
  return (
    <main className="mx-auto max-w-5xl p-5 sm:p-10 text-gray-900">
      {draft && (
        <p
          className="mb-6 rounded border border-amber-400 bg-amber-50 p-4"
          role="note"
        >
          Review only · Proposed agreement and example pricing · Not effective ·
          No charges or acceptance records
        </p>
      )}
      <h1 className="text-3xl font-semibold">Review your business Order</h1>
      <p className="mt-2">
        {order.organization} · {order.locations.length} authorized location(s) ·{' '}
        {order.currency}
      </p>
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <section
          className="rounded-lg border p-5"
          aria-labelledby="recurring-title"
        >
          <h2 id="recurring-title" className="text-xl font-semibold">
            Recurring charges and when they start
          </h2>
          <p className="mt-4">
            Main plan:{' '}
            <strong>
              {money(order.main_plan.price_cents, order.currency)}/month
            </strong>
          </p>
          <p>Main plan billing starts {order.main_plan.billing_start}.</p>
          <p>
            Minimum term: {order.main_plan.minimum_term}; begins{' '}
            {order.main_plan.minimum_term_start}.
          </p>
          <p>Renewal: {order.main_plan.renewal}.</p>
          {order.integrations.map((line) => (
            <div
              key={`${line.provider}-${line.location_id}`}
              className="mt-4 border-t pt-4"
            >
              <h3 className="font-semibold">
                {line.provider === 'opentable' ? 'OpenTable' : 'Resy'}{' '}
                integration —{' '}
                {order.locations.find((l) => l.id === line.location_id)?.name}
              </h3>
              <p>
                {money(line.price_cents, order.currency)} per connected
                location/month, plus applicable taxes.
              </p>
              {line.provider === 'opentable' &&
              line.billing_start ===
                'production_access_enabled_and_successfully_verified' ? (
                <p>
                  Billing starts when this location’s OpenTable production
                  access is enabled and successfully verified, even if AskArthur
                  has not gone live. It renews monthly until cancelled. This is
                  the same integration fee that continues after launch. It does
                  not start the main plan or its minimum term.
                </p>
              ) : (
                <p>Billing starts {line.billing_start}.</p>
              )}
            </div>
          ))}
          <p className="mt-4 border-t pt-4 font-semibold">
            Monthly total after all selected services start:{' '}
            {money(total, order.currency)}
          </p>
          <p>{order.tax_disclosure}</p>
          <p className="mt-3">Usage: {order.usage_schedule}</p>
          <p>Discounts and waivers: {order.discounts_and_waivers}</p>
          <p>Negotiated terms: {order.negotiated_terms}</p>
        </section>
        <section className="rounded-lg border p-5" aria-labelledby="due-title">
          <h2 id="due-title" className="text-xl font-semibold">
            Due now at payment checkout
          </h2>
          <p className="mt-4 text-3xl font-semibold">
            {money(order.due_now_cents, order.currency)}
          </p>
          <p className="mt-3">
            {order.checkout_mode === 'setup'
              ? 'No payment is collected in the payment-method setup step. You authorize only the recurring charges shown in this Order.'
              : 'Payment is collected when you confirm with the payment processor. Signing this Order alone does not collect payment.'}
          </p>
          {order.acknowledgments.opentable && (
            <p className="mt-3">
              The OpenTable integration fee may start before your main plan.
              Each location has its own activation date.
            </p>
          )}
          <h3 className="mt-6 font-semibold">Cancel future renewals</h3>
          <p>{order.cancellation_instructions}</p>
        </section>
      </div>
      <details className="mt-6 rounded-lg border p-4">
        <summary className="cursor-pointer font-semibold">
          Read the complete Business Terms ({order.agreement.version})
        </summary>
        <LegalDocument document={order.agreement} draft={draft} />
      </details>
      <form onSubmit={submit} className="mt-6 space-y-4 rounded-lg border p-5">
        <h2 className="text-xl font-semibold">Authorized business signer</h2>
        {(['name', 'email', 'role'] as const).map((key) => (
          <label key={key} className="block">
            <span className="block font-medium">
              {key === 'name'
                ? 'Full name'
                : key === 'email'
                  ? 'Email'
                  : 'Role or title'}
            </span>
            <input
              required
              type={key === 'email' ? 'email' : 'text'}
              maxLength={255}
              value={signer[key]}
              onChange={(e) =>
                setSigner((s) => ({ ...s, [key]: e.target.value }))
              }
              className="mt-1 w-full rounded border p-3"
            />
          </label>
        ))}
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 h-5 w-5 shrink-0"
          />
          <span>{order.acknowledgments.business}</span>
        </label>
        <p>
          <a
            href={order.privacy_notice.url}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Read the Privacy Policy
          </a>
          . This acknowledgment does not grant marketing or caller recording
          consent.
        </p>
        {order.acknowledgments.opentable && (
          <label className="flex items-start gap-3 rounded border border-amber-400 bg-amber-50 p-4">
            <input
              type="checkbox"
              checked={otAgreed}
              onChange={(e) => setOtAgreed(e.target.checked)}
              className="mt-1 h-5 w-5 shrink-0"
            />
            <span>{order.acknowledgments.opentable}</span>
          </label>
        )}
        {error && (
          <p role="alert" className="rounded bg-red-50 p-3 text-red-900">
            {error}
          </p>
        )}
        {done ? (
          <p role="status">
            Your acceptance was recorded. No payment was collected on this page.
          </p>
        ) : (
          <button
            disabled={busy}
            className="rounded bg-gray-900 px-5 py-3 font-semibold text-white disabled:opacity-50"
            type="submit"
          >
            {draft ? 'Check review acknowledgments' : 'Accept business Order'}
          </button>
        )}
      </form>
    </main>
  );
}
